import {create} from 'zustand/react';
import type {IGridLayout, IPollingData, IStreamingState, StreamingInfo} from '../pages/stream/types';
import callApi from '../apis/callApi.ts';
import {jwtDecode} from 'jwt-decode';
import type {TokenData} from '../pages/stream/types/lib-jitsi-meet';

import {getValidCachedToken, setCachedJitsiToken} from "../utils/StreamingUtil.ts";

export interface IStreamingStore {
    // Polling data
    isHlsServerOn: boolean;
    setIsHlsServerOn: (val: boolean) => void;
    scheduledData: Array<StreamingInfo>;
    setScheduledData: (val: Array<StreamingInfo>) => void;
    liveUrl: string;
    setLiveUrl: (val: string) => void;
    streamingPollingData: IPollingData | null;
    setStreamingPollingData: (val: IPollingData) => void;

    // JWT token management
    setJitsiToken: (centerId: string) => Promise<string>;

    // Streaming state
    state: IStreamingState;
    setStreamingState: (next: Partial<IStreamingState>) => void;

    // Additional state management
    isConnected: boolean;
    setIsConnected: (connected: boolean) => void;

    connectionError: boolean;
    setConnectionError: (error: boolean) => void;

    // Track management state
    localTracksReady: boolean;
    setLocalTracksReady: (ready: boolean) => void;

    // Room info
    participantCount: number;
    setParticipantCount: (count: number) => void;

    // Media controls
    isMicMuted: boolean;
    setIsMicMuted: (muted: boolean) => void;

    isCameraEnabled: boolean;
    setIsCameraEnabled: (enabled: boolean) => void;

    // Reset function
    resetStreamingState: () => void;

    // grid-layout
    gridLayout: IGridLayout;
    setGridLayout: (val: IGridLayout) => void;

    // splash screen
    isSplashOn: boolean;
    setIsSplash: (val: boolean) => void;
}

const initialState: IStreamingState = {
    appId: '',
    room: '',
    jwt: '',
    connection: undefined,
    conference: undefined,
    username: '',
};

const useStreamingStore = create<IStreamingStore>((set, get) => ({
    isHlsServerOn: false,
    setIsHlsServerOn: (val: boolean) => {
        set({isHlsServerOn: val});
    },
    scheduledData: [],
    setScheduledData: (val: Array<StreamingInfo>) => {
        set({scheduledData: val});
    },
    liveUrl: '',
    setLiveUrl: (val: string) => {
        set({liveUrl: val});
    },
    streamingPollingData: null,
    setStreamingPollingData: (val: IPollingData) => {
        set({streamingPollingData: val});
    },
    setJitsiToken: async (val: string) => {
        // [gs #53] moderator 토큰은 메모리에만 둔다(StreamingUtil의 캐시). 캐시가 비어 있거나
        // 만료됐으면(기기 재시작·새로고침 포함) 아래 재발급 경로로 그대로 이어진다.
        const existingToken = getValidCachedToken();
        if (existingToken) {
            console.log('기존 토큰 사용');
            return existingToken;
        }
        try {
            const {data} = await callApi().post(`jitsi/token/`, {
                user: val,
            });
            const newToken = data.token.slice(2, -1);
            setCachedJitsiToken(newToken);
            // [gs #53] 토큰 값 자체는 로그로 남기지 않는다 — 콘솔·화면 캡처만으로
            // moderator 권한 토큰이 그대로 유출된다. 발급 성공 사실만 남긴다.
            console.log('Jitsi 토큰 발급 완료');
            const decoded: TokenData = jwtDecode(newToken);
            set((prev) => ({
                state: {
                    ...prev.state,
                    username: decoded.context.user.name,
                    appId: import.meta.env.VITE_ICSC_APP_ID,
                    room: import.meta.env.VITE_ROOM_NAME,
                    jwt: newToken,
                },
            }));
            return newToken;
        } catch (error) {
            console.error('Jitsi Token fetching failed:', error);
            return '';
        }
    },
    state: {
        appId: import.meta.env.VITE_ICSC_APP_ID,
        room: import.meta.env.VITE_ROOM_NAME,
        // [gs #53] 토큰을 디스크에 두지 않으므로 기동 직후에는 항상 비어 있다.
        // StreamingPage/Watching의 fetchData가 setJitsiToken으로 발급받아 여기에 채운다.
        jwt: getValidCachedToken(),
        connection: undefined,
        conference: undefined,
        username: '',
    },
    setStreamingState: (next) => {
        set((prev) => ({
            state: {
                ...prev.state,
                ...next,
            },
        }));
    },
    isConnected: false,
    setIsConnected: (val: boolean) => {
        set({isConnected: val});
    },
    connectionError: false,
    setConnectionError: (val: boolean) => {
        set({connectionError: val});
    },
    localTracksReady: false,
    setLocalTracksReady: (val: boolean) => {
        set({localTracksReady: val});
    },
    participantCount: 0,
    setParticipantCount: (val: number) => {
        set({participantCount: val});
    },
    isMicMuted: true,
    setIsMicMuted: (val: boolean) => {
        set({isMicMuted: val});
    },
    isCameraEnabled: false,
    setIsCameraEnabled: (val: boolean) => {
        set({isCameraEnabled: val});
    },
    resetStreamingState: () => {
        set(
            {
                state: initialState,
                isConnected: false,
                connectionError: false,
                localTracksReady: false,
                participantCount: 0,
                isMicMuted: true,
                isCameraEnabled: true,
                streamingPollingData: null,
            },
            false
        );
    },
    gridLayout: {mode: '28', sub: true},
    setGridLayout: (val: IGridLayout) => {
        set({gridLayout: val});
    },
    isSplashOn: false,
    setIsSplash: (val: boolean) => {
        set({isSplashOn: val});
    },
}));

export default useStreamingStore;
